import React, {FC, useEffect, useState} from "react"
import apiService from "../services/api-service"
import {AdditionalServiceDto, KCTEventDto} from "../types/contracts"
import Spinner from "./Spinner";

interface UpsellSelectorProps {
    onSelect: (value: string) => void;
}

export const UpsellSelector: FC<UpsellSelectorProps> = ({onSelect}) => {
    const [event, setEvent] = useState<KCTEventDto | null>(null);
    const [events, setEvents] = useState<KCTEventDto[]>([]);
    const [selectedServiceId, setSelectedServiceId] = useState<string>("");
    const [selectedEventId, setSelectedEventId] = useState<string>("");
    const [isLoadingEvents, setIsLoadingEvents] = useState(false);
    const [isLoadingEventDetails, setIsLoadingEventDetails] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isLoading = isLoadingEvents || isLoadingEventDetails;

    useEffect(() => {
        const loadEvents = async () => {
            setIsLoadingEvents(true);
            const eventListResponse = await apiService.getEvents();
            if (eventListResponse.status === 200 && eventListResponse.data) {
                const data = eventListResponse.data as KCTEventDto[];
                setEvents(data);

                // Automatically select the first event if available
                if (data.length > 0) {
                    setSelectedEventId(data[0].id);
                }
            }
            setIsLoadingEvents(false);
        };

        loadEvents();
    }, []);

    useEffect(() => {
        if (!selectedEventId) return;

        const loadEventDetails = async () => {
            setIsLoadingEventDetails(true);
            const eventDetailsResponse = await apiService.getEvent(selectedEventId);
            if (eventDetailsResponse.status === 200 && eventDetailsResponse.data) {
                const data = eventDetailsResponse.data as KCTEventDto;
                setEvent(data);
                if (data.additionalServices.length > 0) {
                    setSelectedServiceId(data.additionalServices[0].id);
                }
            }
            setIsLoadingEventDetails(false);
        };

        loadEventDetails();
    }, [selectedEventId]);

    const handleButtonClick = async () => {
        setIsSubmitting(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 500));
            onSelect(selectedServiceId);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
            <div className="w-full max-w-xs mx-auto">
                {/* Event Selection */}
                <label
                    htmlFor="event"
                    className="block text-sm font-medium text-gray-700"
                >
                    Akce
                </label>
                <select
                    id="event"
                    name="event"
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    value={selectedEventId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedEventId(e.target.value)}
                    disabled={isLoadingEvents}
                >
                    {isLoadingEvents ? (
                        <option>Načítání akce...</option>
                    ) : (
                        events.map((ev) => (
                            <option key={ev.id} value={ev.id}>
                                {ev.name}
                            </option>
                        ))
                    )}
                </select>

                {/* Additional Services Selection */}
                <>
                    <label
                        htmlFor="additionalServices"
                        className="block text-sm font-medium text-gray-700 mt-4"
                    >
                        Výběr doplňkových služeb
                    </label>
                    <select
                        id="additionalServices"
                        name="additionalServices"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={selectedServiceId}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedServiceId(e.target.value)}
                        disabled={!event || isLoadingEventDetails}
                    >
                        {!event || isLoadingEventDetails ? (
                            <option>Načítání služeb...</option>
                        ) : (
                            event?.additionalServices.map((service: AdditionalServiceDto) => (
                                <option key={service.id} value={service.id}>
                                    {service.name}
                                </option>
                            ))
                        )}
                    </select>
                </>

                {/* Submit Button */}
                <button
                    onClick={handleButtonClick}
                    disabled={isLoading || isSubmitting}
                    className={`w-full mt-4 py-2 rounded font-medium text-white focus:outline-none focus:ring ${
                        isLoading || isSubmitting
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-gray-800 py-2 rounded hover:bg-blue-600"
                    }`}
                >
                    {isSubmitting ? (
                        <div className="flex items-center justify-center gap-2">
                            <Spinner size={20} />
                            Vybirání...
                        </div>
                    ) : (
                        "Vybrat"
                    )}
                </button>
            </div>
        </div>
    );
};
